/**
 * Content-notification tap handler.
 *
 * Web port of Flutter's `PushNavigationService._onContentNotificationTap`
 * (`lib/services/push_navigation_service.dart:362-417`) — the busiest branch of
 * the notification switch: it navigates to the tapped notification's content and
 * optionally auto-opens ONE sheet on arrival.
 *
 * Excluded / deviating from the Flutter original:
 *  - The `isPush` fork (`:385-399`) is NOT ported — only the `else` branch
 *    (`:400-415`, `navigateToContentListScreen`). Authorised by task-prompt
 *    line 3 ("Ignore the push-notification side… `isPush` is always false").
 *  - Flutter's stub `UserMedia` + `medias: [media]` array (`:368-378`, `:409`) is
 *    a deliberate web deviation (story Context assumption / Task 5): the stub
 *    exists for Flutter's hero animation and instant pre-fetch render, whereas
 *    all existing web `navigateToContentDetailsByPath` call sites navigate by
 *    path alone and let `ContentDetailsPage` fetch the real document. Loading
 *    presentation is an allowed UI deviation; Flutter itself replaces the stub
 *    with the fetched doc before the auto-open runs
 *    (`content_list_pager_screen.dart:154-160`).
 *
 * Callers — the THIRTEEN `AppNotificationType` cases the Task 13 router delegates
 * here, with their flag combinations (this list must match that router's case
 * table exactly):
 *  - likes only  (1): `contentLike` (`:30-38`).
 *  - comments only + `commentInfo` (4): `contentComment` (`:39-48`),
 *    `commentLike` (`:49-58`), `commentReply` (`:59-68`),
 *    `commentMention` (`:150-159`).
 *  - gifts only  (2): `contentGift` (`:69-77`), `unlockContent` (`:194-202`).
 *  - all three flags false (6): `contentMention` (`:141-149`),
 *    `reportContent` (`:175-183`), `scheduleContentPublished` (`:220-228`),
 *    `payPremiumVideo` (`:301-309`), `payBoostVideo` (`:337-345`),
 *    `earlyAccessContentReceived` (`:328-336`).
 * 1 + 4 + 2 + 6 = 13. Flutter's two remaining `_onContentNotificationTap` cases,
 * `contentAdded` (`:234-242`) and `earlyAccessContentAdded` (`:319-327`), are
 * NOT callers: both types are filtered out of the inbox list in both clients
 * (`messages_screen.dart:177-185`; `useInboxNotifications.ts:83-84`,
 * `subscribeToNotificationsUseCase.ts:78-79`) and have no row widget, so their
 * taps are structurally unreachable (task-prompt line 21).
 *
 * Pending caller: `notificationTap/notificationTapRouter.ts` (Task 13) is the
 * first and only consumer — this module is intentionally uncalled for exactly
 * one task, not dead code.
 */
import { Navigator } from '@/presentation/routing/Navigator';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

/**
 * The three deep-link auto-open flags Flutter's content pager accepts
 * (`push_navigation_service.dart:363-365`). The destination enforces Flutter's
 * precedence — comments > likes > gifts — so at most ONE sheet opens even if
 * several are set.
 */
export interface ContentNotificationTapOptions {
  openContentLikesSheet: boolean;
  openCommentsSheet: boolean;
  openGiftsSheet: boolean;
}

export const handleContentNotificationTap = (
  context: NotificationTapContext,
  options: ContentNotificationTapOptions,
): void => {
  const mediaInfo = context.notification.info.mediaInfo;
  // Flutter null-asserts `notification.info.mediaInfo!` (`:370-376`); every
  // notification type routed here is written with a `mediaInfo` block. This
  // guard is the defensive web equivalent of that assertion — a no-op instead
  // of a crash on a malformed document — NOT a behaviour change.
  if (!mediaInfo) return;

  Navigator.navigateToContentDetailsByPath(mediaInfo.path, {
    openContentLikesSheet: options.openContentLikesSheet,
    openCommentsSheet: options.openCommentsSheet,
    openGiftsSheet: options.openGiftsSheet,
    // Forwarded AS-IS: may be `undefined`, never mutated, and never given
    // `highlightParentComment: true` — Flutter's push-navigation path forwards
    // `notification.info.commentInfo` unmodified (`comment_info.dart:10-14`
    // keeps `highlightParentComment` off the wire, defaulting to `false`),
    // unlike the comment-reply chip path at `ContentListItem.tsx:210-217`.
    //
    // Deliberate simplification, currently unobservable: Flutter passes
    // `commentInfo` on only the four comment cases (`:41-46`, `:51-56`,
    // `:61-66`, `:152-157`) and leaves it null everywhere else, while the web
    // forwards it on all thirteen. That is behaviourally identical today
    // because the destination reads `commentInfo` ONLY inside its
    // `openCommentsSheet` branch (`ContentDetailsPage`, Task 5's three-way
    // effect — `content_list_pager_screen.dart:98-100`), and only those four
    // cases set that flag. IF the deferred screen-scoped-`commentInfo` work
    // (Task 5 — the comment-icon opener reusing a deep link's `commentInfo`)
    // ever lands, this forwarding becomes observable — e.g. a `contentLike`
    // row whose doc happens to carry `info.commentInfo` would highlight a
    // comment Flutter would not — and MUST then be narrowed to those four
    // comment cases.
    commentInfo: context.notification.info.commentInfo,
  });
};
